import { forwardRef, useImperativeHandle, useMemo } from 'react';
import {
  DropdownList,
  PhoneNumberInput,
  Stack,
  Text,
  TextInput,
} from '@adc/parallax-component-library';
import { useForm, useStore } from '@tanstack/react-form';
import type { z } from 'zod';

import { useAllDepartments } from '~/api/query/departmentAll';
import { DropdownInput } from '~/components/DropdownInput';
import {
  ALLOWED_ROLE_CREATION,
  USER_ROLE_NAME,
  USER_ROLES,
} from '~/features/auth/roles';
import { useAuthState } from '~/features/auth/useAuthState';
import { buildTestId } from '~/utils/buildTestId';
import { getErrorProps } from '~/utils/forms/getErrorProps';

import { type basicUserSchema, userSchema, validRoles } from './validations';

export type UserSubmitValues = z.infer<typeof userSchema>;
type BasicFormValues = z.input<typeof basicUserSchema>;

export type AdminUserFormRef = {
  submitForm: () => Promise<void>;
};

type AdminUserFormProps = {
  testID: string;
  onSubmit: (values: UserSubmitValues) => Promise<void>;
  defaultValues?: Partial<UserSubmitValues>;
};

const baseDefaultValues: BasicFormValues = {
  email: '',
  phone: '',
  firstName: '',
  lastName: '',
};

export const AdminUserForm = forwardRef<AdminUserFormRef, AdminUserFormProps>(
  ({ testID, onSubmit, defaultValues: passedDefaults }, ref) => {
    const { data: departments = [], isLoading: isDepartmentsLoading } =
      useAllDepartments();

    const form = useForm({
      defaultValues: {
        ...baseDefaultValues,
        ...passedDefaults,
      },
      validators: { onChange: userSchema },
      onSubmit: ({ value }) => onSubmit(userSchema.parse(value)),
    });

    const { userInfo } = useAuthState();

    const roleItems = useMemo(() => {
      if (!userInfo?.role) {
        return [];
      }

      const allowedRoleValues = ALLOWED_ROLE_CREATION[userInfo.role];

      return allowedRoleValues
        .filter((role) => validRoles.includes(role))
        .map((role) => ({
          value: role,
          label: USER_ROLE_NAME[role],
        }));
    }, [userInfo?.role]);

    const { departmentId: mainDepartmentValue, role: roleValue } = useStore(
      form.store,
      (s) => s.values,
    );

    const isHcpAdmin = roleValue === USER_ROLES.HcpAdmin;

    useImperativeHandle(
      ref,
      () => ({
        submitForm: form.handleSubmit,
      }),
      [form.handleSubmit],
    );

    const departmentListItems = useMemo(
      () => departments.map((d) => d.department),
      [departments],
    );

    const { departmentSelectedIndex, departmentData } = useMemo(() => {
      let selectedIndex: number | undefined;
      let label: string | undefined;

      if (isHcpAdmin) {
        selectedIndex = 0;
        label = 'All Departments';
      } else if (mainDepartmentValue) {
        const depIndex = departments.findIndex(
          (d) => d.departmentId === mainDepartmentValue,
        );

        selectedIndex = depIndex >= 0 ? depIndex + 1 : undefined;
        label =
          departments.find((d) => d.departmentId === mainDepartmentValue)
            ?.department ?? '';
      }

      return { departmentSelectedIndex: selectedIndex, departmentData: label };
    }, [isHcpAdmin, mainDepartmentValue, departments]);

    return (
      <Stack gap="$6" paddingTop="$2" testID={testID}>
        <Stack flexDirection="row" gap="$6">
          <Stack flex={1}>
            <form.Field name="firstName">
              {(field) => (
                <TextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'first', 'name'],
                  })}
                  aria-label="First name"
                  label="First Name"
                  placeholder="First"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  inputMode="text"
                  autoCapitalize="none"
                  autoCorrect={false}
                  {...getErrorProps(field)}
                />
              )}
            </form.Field>
          </Stack>
          <Stack flex={1}>
            <form.Field name="lastName">
              {(field) => (
                <TextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'last', 'name'],
                  })}
                  aria-label="Last name"
                  label="Last Name"
                  placeholder="Last"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  inputMode="text"
                  autoCapitalize="none"
                  autoCorrect={false}
                  {...getErrorProps(field)}
                />
              )}
            </form.Field>
          </Stack>
        </Stack>

        <Text type="$bodyBase.medium" color="$text.100">
          Contact Information
        </Text>
        <form.Field name="phone">
          {(field) => (
            <PhoneNumberInput
              testID={buildTestId({ testID, suffix: ['input', 'phone'] })}
              aria-label="Phone Number (Optional)"
              label="Phone Number (Optional)"
              placeholder="Phone"
              value={field.state.value}
              onChangeText={field.handleChange}
              onBlur={field.handleBlur}
              inputMode="tel"
              autoCapitalize="none"
              autoCorrect={false}
              {...getErrorProps(field)}
            />
          )}
        </form.Field>

        <form.Field name="email">
          {(field) => (
            <TextInput
              testID={buildTestId({ testID, suffix: ['input', 'email'] })}
              aria-label="Email Address"
              label="Email Address"
              placeholder="Email"
              value={field.state.value}
              onChangeText={field.handleChange}
              onBlur={field.handleBlur}
              inputMode="email"
              autoCapitalize="none"
              autoCorrect={false}
              {...getErrorProps(field)}
            />
          )}
        </form.Field>

        <Text type="$bodyBase.medium" color="$text.100">
          Access
        </Text>
        <form.Field name="role">
          {(field) => (
            <DropdownList
              testID={buildTestId({ testID, suffix: ['dropdown', 'role'] })}
              items={roleItems.map((i) => i.label)}
              selectedIndex={
                field.state.value
                  ? roleItems.findIndex((i) => i.value === field.state.value)
                  : undefined
              }
              onSelect={(index) => {
                const selected = roleItems[index]?.value;

                if (selected) {
                  field.handleChange(selected);
                  form.setFieldValue(
                    'departmentId',
                    form.getFieldValue('departmentId'),
                  );
                }
              }}
            >
              <DropdownInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'type', 'role'],
                })}
                aria-label="User Type"
                label="User Type"
                placeholder="Select"
                value={
                  field.state.value
                    ? (roleItems.find((i) => i.value === field.state.value)
                        ?.label ?? '')
                    : ''
                }
                {...getErrorProps(field)}
              />
            </DropdownList>
          )}
        </form.Field>

        <form.Field name="departmentId">
          {(field) => (
            <DropdownList
              testID={buildTestId({
                testID,
                suffix: ['dropdown', 'department', 'access'],
              })}
              items={isHcpAdmin ? ['All Departments'] : departmentListItems}
              selectedIndex={departmentSelectedIndex}
              onSelect={(index) => {
                if (index === 0) {
                  field.handleChange(undefined);
                } else {
                  const selectedId = departments[index - 1]?.departmentId;

                  if (selectedId !== undefined) {
                    field.handleChange(selectedId);
                  }
                }
              }}
            >
              <DropdownInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'type', 'department', 'access'],
                })}
                aria-label="Department Access"
                label="Department Access"
                placeholder="Select"
                disabled={isHcpAdmin}
                value={departmentData}
                {...getErrorProps(field)}
              />
            </DropdownList>
          )}
        </form.Field>

        {isHcpAdmin && (
          <form.Field name="departmentId">
            {(field) => {
              const mainDeptItems = !Number(passedDefaults?.departmentId)
                ? ['None', ...departments.map((d) => d.department)]
                : departments.map((d) => d.department);
              const selectedIndex =
                typeof field.state.value === 'number'
                  ? departments.findIndex(
                      (d) => d.departmentId === field.state.value,
                    ) + 1
                  : 0;

              return (
                <DropdownList
                  testID={buildTestId({
                    testID,
                    suffix: ['dropdown', 'department'],
                  })}
                  items={mainDeptItems}
                  selectedIndex={selectedIndex}
                  onSelect={(index) => {
                    if (index === 0) {
                      field.handleChange(undefined);
                    } else {
                      const selectedId = departments[index - 1]?.departmentId;

                      if (selectedId !== undefined) {
                        field.handleChange(selectedId);
                      }
                    }
                    field.handleBlur();
                  }}
                >
                  <DropdownInput
                    testID={buildTestId({
                      testID,
                      suffix: ['input', 'type', 'main', 'department'],
                    })}
                    aria-label="Primary Department (Optional)"
                    label="Primary Department (Optional)"
                    placeholder={isDepartmentsLoading ? 'Loading...' : 'Select'}
                    value={
                      typeof field.state.value === 'number'
                        ? (departments.find(
                            (d) => d.departmentId === field.state.value,
                          )?.department ?? '')
                        : ''
                    }
                  />
                </DropdownList>
              );
            }}
          </form.Field>
        )}
      </Stack>
    );
  },
);

AdminUserForm.displayName = 'AdminUserForm';
